import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Coins, ExternalLink } from "lucide-react";
import { I18nProvider } from "@/lib/i18n";
import { AssetContributionPanel } from "@/components/villagepando/AssetContributionPanel";
import { VP_ASSETS, type AssetSlug } from "@/lib/villagepando-assets";
import { EcosystemHub, EcosystemSwitcher, type EcosystemNodeId } from "@/components/EcosystemHub";
import { absUrl } from "@/lib/seo-jsonld";

export const Route = createFileRoute("/villagepando/$asset")({
  loader: ({ params }) => {
    const asset = VP_ASSETS[params.asset as AssetSlug];
    if (!asset) throw notFound();
    return { asset };
  },
  head: ({ loaderData, params }) => {
    const a = loaderData?.asset;
    const title = a
      ? `${a.name} — Milestone Contribution · VillagePando`
      : "VillagePando";
    const desc = a
      ? `${a.tagline} Milestone-based direct funding into the ${a.name} project treasury — no DEX, no slippage, asset-backed.`
      : "VillagePando asset module.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: absUrl(`/villagepando/${params.asset}`) },
        { property: "og:image", content: absUrl("/og-image.jpg") },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: absUrl("/og-image.jpg") },
        ...(a ? [] : [{ name: "robots", content: "noindex" }]),
      ],
      links: a ? [{ rel: "canonical", href: absUrl(`/villagepando/${params.asset}`) }] : [],
    };
  },
  notFoundComponent: () => (
    <div className="grid min-h-screen place-items-center bg-[oklch(0.10_0.012_260)] p-6 text-center text-foreground">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">404</p>
        <h1 className="mt-2 font-display text-3xl">Unknown VillagePando asset</h1>
        <Link
          to="/villagepando"
          className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em]"
        >
          <ArrowLeft className="h-3 w-3" /> Back to dashboard
        </Link>
      </div>
    </div>
  ),
  errorComponent: ({ reset }) => (
    <div className="grid min-h-screen place-items-center bg-[oklch(0.10_0.012_260)] p-6 text-center text-foreground">
      <div>
        <h1 className="font-display text-2xl">Something went wrong.</h1>
        <button
          type="button"
          onClick={reset}
          className="mt-4 rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em]"
        >
          Retry
        </button>
      </div>
    </div>
  ),
  component: AssetRoute,
});

function AssetRoute() {
  const { asset } = Route.useLoaderData();
  const activeId = asset.slug as EcosystemNodeId;
  return (
    <I18nProvider>
      <div className="relative min-h-screen overflow-x-hidden bg-[oklch(0.10_0.012_260)] text-foreground">
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 -z-10"
          style={{
            background: `radial-gradient(70% 50% at 80% -10%, ${asset.color.replace(")", " / 0.10)")}, transparent 60%), radial-gradient(40% 30% at 15% 110%, ${asset.color.replace(")", " / 0.08)")}, transparent 70%)`,
          }}
        />
        <header className="fixed left-0 right-0 top-0 z-40">
          <div className="mx-4 mt-4 flex max-w-7xl items-center justify-between rounded-full border border-white/10 bg-[oklch(0.12_0.012_260_/_0.7)] px-4 py-2.5 backdrop-blur-xl sm:mx-auto sm:px-5">
            <Link to="/villagepando" className="flex items-center gap-2.5">
              <span
                className="grid h-8 w-8 place-items-center rounded-full border bg-white/[0.04]"
                style={{ borderColor: `${asset.color.replace(")", " / 0.4)")}` }}
              >
                <Coins className="h-4 w-4" style={{ color: asset.color }} />
              </span>
              <div className="leading-tight">
                <div className="font-display text-sm font-bold tracking-tight">
                  VillagePando · {asset.name}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
                  Milestone contribution
                </div>
              </div>
            </Link>
            <div className="flex items-center gap-2">
              <EcosystemSwitcher activeId={activeId} />
              <Link
                to="/villagepando"
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/80 hover:bg-white/[0.08]"
              >
                <ArrowLeft className="h-3 w-3" />
                Dashboard
              </Link>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-7xl px-4 pb-24 pt-28 sm:px-6">
          <div className="mb-10 max-w-2xl">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-foreground/60">
              // {asset.name} · Asset Subclass
            </span>
            <h1 className="mt-2 font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl">
              Fund the next milestone.
              <br />
              <span className="text-foreground/60">Mint asset-backed {asset.symbol}.</span>
            </h1>
            <p className="mt-4 text-base text-muted-foreground">{asset.tagline}</p>
          </div>

          <AssetContributionPanel asset={asset} />

          <p className="mt-10 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <ExternalLink className="h-3 w-3" />
            All contributions route to a hardcoded project treasury — never to an open-market DEX.
          </p>

          <div className="mt-12">
            <EcosystemHub activeId={activeId} />
          </div>
        </main>
      </div>
    </I18nProvider>
  );
}
